import { WebRTCAdaptor } from "@antmedia/webrtc_adaptor";
import type { CallConfig } from "../api";

export type CallStatus =
  | "idle"
  | "requesting_media"
  | "connecting"
  | "publishing"
  | "waiting_remote"
  | "ringing"      // consumer's stream is published; waiting for the LO to pick up
  | "in_call"      // LO has answered; both sides actively exchanging media
  | "busy"         // LO is on another 1:1 call; Ant Media rejected our publish
  | "ended"
  | "error";

export interface ChatMsg {
  id: string;
  from: "me" | "remote";
  text: string;
  at: number;
}

export interface CallCallbacks {
  onStatus: (s: CallStatus, detail?: string) => void;
  onLocalStream: (stream: MediaStream | null) => void;
  onRemoteStream: (stream: MediaStream | null) => void;
  onChat: (msg: ChatMsg) => void;
  onError: (err: string) => void;
}

interface AdaptorInfoEvent {
  attributes?: Record<string, unknown>;
  streamId?: string;
}

const REMOTE_PROBE_INTERVAL_MS = 4000;

/**
 * Thin React-friendly wrapper around Ant Media's WebRTCAdaptor for a 1-to-1
 * consumer <-> loan-officer call.
 *
 * Streaming model:
 *   - Consumer publishes a stream named `consumer_<random>` to the `live` app.
 *   - Consumer subscribes (plays) the well-known stream id provided by the
 *     server (typically `lo_<web_app_id>`); the loan officer publishes that
 *     stream from their own page (out of scope for this build).
 *   - Chat is exchanged via the data channel that Ant Media attaches to each
 *     published/played stream.
 */
export class CallSession {
  private adaptor: WebRTCAdaptor | null = null;
  private localStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;
  private status: CallStatus = "idle";
  private remoteProbeTimer: number | null = null;
  private destroyed = false;

  readonly publishStreamId: string;
  readonly subscribeStreamId: string;

  constructor(
    private cfg: CallConfig,
    private cb: CallCallbacks,
  ) {
    // Both stream IDs are deterministic from the LO's web_app_id.
    // The LO console publishes lo_stream_id; we publish consumer_stream_id.
    // Each side plays the other's id. Only one consumer call per LO at a time.
    this.publishStreamId = cfg.consumer_stream_id;
    this.subscribeStreamId = cfg.lo_stream_id;
  }

  async start(): Promise<void> {
    this.setStatus("requesting_media");
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480 },
        audio: true,
      });
      this.cb.onLocalStream(this.localStream);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      this.cb.onError(`Microphone/camera access denied: ${msg}`);
      this.setStatus("error", msg);
      return;
    }

    this.setStatus("connecting");

    this.adaptor = new WebRTCAdaptor({
      websocket_url: this.cfg.wss_url,
      mediaConstraints: { video: true, audio: true },
      peerconnection_config: { iceServers: this.cfg.ice_servers as RTCIceServer[] },
      sdp_constraints: { OfferToReceiveAudio: true, OfferToReceiveVideo: true },
      localVideoElement: undefined,
      isPlayMode: false,
      debug: false,
      callback: (info: string, obj?: AdaptorInfoEvent) => this.handleInfo(info, obj),
      callbackError: (err: string, message?: string) => {
        // Many of these are non-fatal (e.g. "no_stream_exist" while polling for the LO),
        // so we surface them as status detail rather than tearing down the call.
        if (err === "no_stream_exist") {
          this.setStatus("waiting_remote", "Loan officer hasn't joined yet.");
          return;
        }
        // The probe timer occasionally double-calls play() before the first
        // negotiation finishes; that's a benign race, not an error worth showing.
        if (err === "already_playing" || err === "alreadyPlaying") {
          return;
        }
        // Stream-id collision = LO already has another consumer publishing.
        // Ant Media reports this with one of several names depending on version.
        if (
          err === "streamIdInUse" ||
          err === "alreadyPlaying" ||
          err === "alreadyPublishing" ||
          (typeof message === "string" && /already.*publish|in use/i.test(message))
        ) {
          // Tear down our half of the connection so we don't keep retrying.
          try { this.adaptor?.stop(this.publishStreamId); } catch { /* noop */ }
          if (this.localStream) {
            this.localStream.getTracks().forEach((t) => t.stop());
            this.localStream = null;
            this.cb.onLocalStream(null);
          }
          this.setStatus("busy", "Loan officer is on another call.");
          return;
        }
        const text = message ? `${err}: ${message}` : err;
        this.cb.onError(text);
        this.setStatus("error", text);
      },
    });
  }

  end(): void {
    this.destroyed = true;
    if (this.remoteProbeTimer) {
      window.clearInterval(this.remoteProbeTimer);
      this.remoteProbeTimer = null;
    }
    try { this.adaptor?.stop(this.publishStreamId); } catch { /* noop */ }
    try { this.adaptor?.stop(this.subscribeStreamId); } catch { /* noop */ }
    try { this.adaptor?.closeWebSocket(); } catch { /* noop */ }
    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => t.stop());
    }
    this.localStream = null;
    this.remoteStream = null;
    this.cb.onLocalStream(null);
    this.cb.onRemoteStream(null);
    this.setStatus("ended");
  }

  toggleAudio(muted: boolean): void {
    this.localStream?.getAudioTracks().forEach((t) => (t.enabled = !muted));
  }

  toggleVideo(off: boolean): void {
    this.localStream?.getVideoTracks().forEach((t) => (t.enabled = !off));
  }

  sendChat(text: string): void {
    const trimmed = text.trim();
    if (!trimmed || !this.adaptor) return;
    const payload = JSON.stringify({ kind: "chat", text: trimmed, at: Date.now() });
    try {
      // Send on our published stream so the LO subscriber receives it.
      this.adaptor.sendData(this.publishStreamId, payload);
    } catch { /* noop */ }
    this.cb.onChat({
      id: crypto.randomUUID(),
      from: "me",
      text: trimmed,
      at: Date.now(),
    });
  }

  private handleInfo(info: string, obj?: AdaptorInfoEvent): void {
    if (this.destroyed || !this.adaptor) return;
    switch (info) {
      case "initialized":
        try {
          this.adaptor.publish(this.publishStreamId);
          this.setStatus("publishing");
          this.beginRemoteProbe();
        } catch (e) {
          this.cb.onError(`publish() failed: ${e}`);
          this.setStatus("error");
        }
        break;
      case "publish_started":
        this.setStatus("waiting_remote", "Waiting for the loan officer to join…");
        break;
      case "play_started":
        // The LO's stream is reachable but tracks may still be disabled
        // (the LO is in standby and hasn't picked up). Stay in "ringing"
        // until we receive the lo_answered control message.
        this.setStatus("ringing", "Calling…");
        this.captureRemoteStream();
        break;
      case "data_received": {
        const raw = (obj as unknown as { data?: unknown; event?: unknown }) ?? {};
        const candidate = raw.data ?? raw.event ?? raw;
        try {
          const text = typeof candidate === "string"
            ? candidate
            : new TextDecoder().decode(candidate as ArrayBuffer);
          const parsed = JSON.parse(text) as { kind?: string; text?: string; at?: number };
          if (parsed.kind === "lo_answered") {
            // LO clicked Pick up; their tracks just turned on. Promote to in_call.
            this.setStatus("in_call");
          } else if (parsed.kind === "lo_ended_call") {
            // LO hung up but stayed online. Tear down our half so the
            // browser releases the camera + mic, then mark "ended" so
            // the modal can close gracefully.
            this.end();
          } else if (parsed.kind === "chat" && typeof parsed.text === "string") {
            this.cb.onChat({
              id: crypto.randomUUID(),
              from: "remote",
              text: parsed.text,
              at: typeof parsed.at === "number" ? parsed.at : Date.now(),
            });
          }
        } catch { /* ignore non-JSON data frames */ }
        break;
      }
      case "play_finished":
      case "publish_finished":
      case "closed":
        if (!this.destroyed) this.setStatus("ended");
        break;
      default:
        break;
    }
  }

  /** Periodically poll for the LO's stream until it appears (or call ends). */
  private beginRemoteProbe(): void {
    if (this.remoteProbeTimer) return;
    const tick = () => {
      if (this.destroyed || !this.adaptor) return;
      // Stop polling as soon as the play has succeeded (status moves to ringing
      // or in_call) -- otherwise we keep firing play() and Ant Media barks back
      // with "already_playing".
      if (this.status === "ringing" || this.status === "in_call" || this.remoteStream) {
        if (this.remoteProbeTimer) {
          window.clearInterval(this.remoteProbeTimer);
          this.remoteProbeTimer = null;
        }
        return;
      }
      try {
        this.adaptor.play(this.subscribeStreamId);
      } catch { /* noop */ }
    };
    tick();
    this.remoteProbeTimer = window.setInterval(tick, REMOTE_PROBE_INTERVAL_MS);
  }

  private captureRemoteStream(): void {
    if (!this.adaptor) return;
    // Ant Media's adaptor emits the remote MediaStream via the same callback
    // mechanism on some versions; on others we have to fish it out of the
    // RTCPeerConnection's receivers. We do the latter for compatibility.
    type AdaptorWithPeers = WebRTCAdaptor & {
      remotePeerConnection?: Record<string, RTCPeerConnection>;
    };
    const a = this.adaptor as AdaptorWithPeers;
    const pcMap = a.remotePeerConnection;
    if (!pcMap) return;
    const pc = pcMap[this.subscribeStreamId];
    if (!pc) return;
    const ms = new MediaStream();
    pc.getReceivers().forEach((r) => {
      if (r.track) ms.addTrack(r.track);
    });
    if (ms.getTracks().length > 0) {
      this.remoteStream = ms;
      this.cb.onRemoteStream(ms);
    }
    // Listen for late tracks
    pc.ontrack = (ev) => {
      const stream = ev.streams[0] ?? this.remoteStream ?? new MediaStream();
      if (!stream.getTracks().includes(ev.track)) stream.addTrack(ev.track);
      this.remoteStream = stream;
      this.cb.onRemoteStream(stream);
    };
  }

  private setStatus(s: CallStatus, detail?: string): void {
    this.status = s;
    this.cb.onStatus(s, detail);
  }
}
